import React, { FC } from 'react';
import { useLazyLoading, Loading } from '../_common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Routes } from '../../utils';
import NotificationStepper from './NotificationStepper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/GridLegacy';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import NotificationStepperForAssetLoad from './NotificationStepperForAssetLoad';

interface LooseObject {
    [key: string]: any;
}

interface NotificationListItemProps {
    abortRequest: () => void;
    notifications: LooseObject[];
    refetch?: () => void;
    isLoading: boolean;
    isEndOfResults: boolean;
    history: any;
    onLoadMore: () => void;
}

const NotificationListItem: FC<NotificationListItemProps> = ({
    abortRequest,
    notifications,
    refetch,
    isLoading,
    isEndOfResults,
    history,
    onLoadMore,
}: NotificationListItemProps): React.ReactElement => {
    const { handleScroll, scrollPos } = useLazyLoading('NotificationList', isLoading, isEndOfResults, onLoadMore);

    if (isLoading) {
        return <Loading />;
    }

    const doc: HTMLElement | null = document.getElementById('NotificationList');
    if (doc) {
        doc.scroll(0, scrollPos);
    }

    const openAssetLoadProfile = (loadId: number): void => {
        const URL = `${window.location.origin}${Routes.EQUIPMENT_MANAGEMENT.route}`;
        window.open(URL + loadId, '_blank');
    };

    const openLoadProfile = (loadId: number): void => {
        const URL = `${window.location.origin}${Routes.LOAD_PROFILE.route}`;
        window.open(URL + loadId, '_blank');
    };

    if (notifications.length > 0) {
        return (
            <div id="NotificationList" className="Notification-List" onScroll={handleScroll}>
                {notifications.map((notification, index) => {
                    switch (notification.notificationType) {
                        case 'LoadTracker':
                            const loadTrackerJSON = JSON.parse(notification.notificationJSON);
                            const loadTrackerData = loadTrackerJSON[0].Load;
                            const assetLoadData = loadTrackerJSON[0];
                            return (
                                <div className="Notifications" key={`${loadTrackerData.LoadId}-${index}`}>
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        className="Notification-Loader-Tracker-Header"
                                    >
                                        {assetLoadData.Comments === "AssetLoad" ? (
                                            <Grid item>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="subtitle1" display="inline">
                                                        {loadTrackerData.LoadId}{' '}
                                                        <span
                                                            className="Load-Link"
                                                            onClick={(): void => openAssetLoadProfile(loadTrackerData.LoadId)}
                                                        >
                                                            <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                                                        </span>
                                                    </Typography>
                                                    <Typography variant="body1" display="inline">
                                                        <span className="SplitLoad">
                                                            Equipment Load
                                                        </span>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Driver:{' '}
                                                    </Typography>
                                                    <Typography display="inline" variant="subtitle1">
                                                        {assetLoadData.DriverName}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Carrier:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {assetLoadData.DriverCompanyName}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Load Type:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {assetLoadData.LoadType}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        ) : (
                                            <Grid item>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="subtitle1" display="inline">
                                                        {loadTrackerData.LoadId}{' '}
                                                        <span
                                                            className="Load-Link"
                                                            onClick={(): void => openLoadProfile(loadTrackerData.LoadId)}
                                                        >
                                                            <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                                                        </span>
                                                    </Typography>
                                                    <Typography variant="body1" display="inline">
                                                        {loadTrackerData.ProppantName}
                                                    </Typography>
                                                    <Typography variant="body1" display="inline">
                                                        <span className="SplitLoad">
                                                            {loadTrackerData.IsSplit ? ' Load Split ' : ''}
                                                        </span>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Customer:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {loadTrackerData.CustomerName}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Job:{' '}
                                                    </Typography>
                                                    <Typography display="inline" variant="subtitle1">
                                                        {loadTrackerData.WellName}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Driver / Split Driver:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {`${loadTrackerData.AssignedToPrimaryDriverName} ${loadTrackerData.SplitDriverName
                                                            ? ' / ' + loadTrackerData.SplitDriverName
                                                            : ''
                                                            }`}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}

                                        {assetLoadData.Comments === "AssetLoad" ? (
                                            <Grid item className="text-align-right">
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        {
                                                            moment
                                                                .utc(notification.notificationDateTime)
                                                                .tz('America/Chicago')
                                                                .format('MMMM DD, YYYY h:mm:ss a')
                                                        }
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Truck:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {assetLoadData.TruckNumber}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Chassis:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {assetLoadData.ChassisNumber}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Status:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {assetLoadData.Status}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        ) : (
                                            <Grid item className="text-align-right">
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        {
                                                            moment
                                                                .utc(notification.notificationDateTime)
                                                                .tz('America/Chicago')
                                                                .format('MMMM DD, YYYY h:mm:ss a')
                                                        }
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Assigned:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {
                                                            moment
                                                                .utc(loadTrackerData.AssignedDateTime)
                                                                .tz('America/Chicago')
                                                                .format('MMMM DD, YYYY h:mm:ss a')
                                                        }
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        P.O.#:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {loadTrackerData.SandPO}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Carrier:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {loadTrackerData.AssignedToPrimaryDriverCompany}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {
                                        assetLoadData.Comments === "AssetLoad" ? (
                                            <NotificationStepperForAssetLoad jsonData={assetLoadData} />
                                        ) : (
                                            <NotificationStepper jsonData={loadTrackerData} />
                                        )
                                    }
                                </div>
                            );
                        case 'ShiftStart':
                            const shifStartData = JSON.parse(notification.notificationJSON);
                            const shifStartDriverData = shifStartData[0].Driver;
                            return (
                                <div className="Notifications" key={`${shifStartDriverData.DriverId}-${index}`}>
                                    <Grid container
                                        // justify="space-between"
                                        justifyContent="space-between"
                                        className="Notification-Shift-Start">
                                        <Grid item>
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="subtitle1" display="inline">
                                                    {`(${shifStartDriverData.DriverId}) ${shifStartDriverData.DriverName}`}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="body1" display="inline">
                                                    Carrier:{' '}
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {shifStartDriverData.DriverCompanyName}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item className="text-align-right">
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="body1" display="inline" gutterBottom>
                                                    {moment
                                                        .utc(shifStartDriverData.ShiftStartDateTime)
                                                        .tz('America/Chicago')
                                                        .format('MMMM DD, YYYY h:mm:ss a')}
                                                </Typography>
                                                <Typography variant="subtitle2" display="inline" gutterBottom>
                                                    CHECK-IN
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="body1" display="inline">
                                                    Truck / Box / Chassis:{' '}
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {`${shifStartDriverData.TruckNumber || 'NONE'} / ${shifStartDriverData.BoxNumber?.startsWith('8888') ? '' : shifStartDriverData.BoxNumber || 'NONE'} / ${shifStartDriverData.ChassisNumber || 'NONE'}`}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            );
                        case 'ShiftEnd':
                            const shifEndData = JSON.parse(notification.notificationJSON);
                            const shifEndDriverData = shifEndData[0].Driver;
                            return (
                                <div className="Notifications" key={`${shifEndDriverData.DriverId}-${index}`}>
                                    <Grid container
                                        // justify="space-between"
                                        justifyContent="space-between"
                                        className="Notification-Shift-Start">
                                        <Grid item>
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="subtitle1" display="inline">
                                                    {`(${shifEndDriverData.DriverId}) ${shifEndDriverData.DriverName}`}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="body1" display="inline">
                                                    Carrier:{' '}
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {shifEndDriverData.DriverCompanyName}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item className="text-align-right">
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="body1" display="inline" gutterBottom>
                                                    {moment
                                                        .utc(shifEndDriverData.ShiftEndDateTime)
                                                        .tz('America/Chicago')
                                                        .format('MMMM DD, YYYY h:mm:ss a')}
                                                </Typography>
                                                <Typography variant="subtitle2" display="inline" gutterBottom>
                                                    CHECK-OUT
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="body1" display="inline">
                                                    Truck / Box / Chassis:{' '}
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {`${shifEndDriverData.TruckNumber || 'NONE '} / ${shifEndDriverData.BoxNumber?.startsWith('8888') ? '' : shifEndDriverData.BoxNumber || 'NONE'} / ${shifEndDriverData.ChassisNumber || 'NONE'}`}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            );
                        case 'RejectedLoad':
                            const jsonData = JSON.parse(notification.notificationJSON);
                            const loadData = jsonData && jsonData[0].Load;
                            if (loadData) {
                                return (
                                    <div className="Notifications" key={`${loadData.DriverId}-${index}`}>
                                        <Grid container
                                            // justify="space-between"
                                            justifyContent="space-between"
                                            className="Notification-Shift-Start">
                                            <Grid item>
                                                <Grid item xs={12} md={12} className="mb-3rem">
                                                    <Typography variant="subtitle1" display="inline">
                                                        {`(${loadData.DriverId}) ${loadData.DriverName}`}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="body1" display="inline">
                                                        Carrier:{' '}
                                                    </Typography>
                                                    <Typography variant="body2" display="inline">
                                                        {loadData.DriverCompanyName}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} md={6} className="text-align-right">
                                                <div className="rejected-load-right">
                                                    <div className="top-row">
                                                        <Typography variant="body1" className="timestamp-text" display="inline" gutterBottom>
                                                            {moment
                                                                .utc(loadData.RejectedDateTime)
                                                                .tz('America/Chicago')
                                                                .format('MMMM DD, YYYY h:mm:ss a')}
                                                        </Typography>
                                                        <Typography variant="subtitle2" className="reject-reason-text" display="inline" gutterBottom>
                                                            {loadData.RejectReason}
                                                        </Typography>
                                                    </div>
                                                    <div className="bottom-row">
                                                        <Typography variant="subtitle1" className="load-id-text" display="inline">
                                                            {loadData.LoadId}{' '}
                                                            <span
                                                                className="Load-Link"
                                                                onClick={(): void => openLoadProfile(loadData.LoadId)}
                                                            >
                                                                <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                                                            </span>
                                                        </Typography>
                                                    </div>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </div>
                                );
                            } else {
                                return (
                                    <div className="Notifications" key={`${notification.loadId}-${index}`}>
                                        <Grid container
                                            //  justify="space-between"
                                            justifyContent="space-between"
                                            className="Notification-Shift-Start">
                                            <Grid item>
                                                <Typography variant="body1">
                                                    No Data found for this load {notification.loadId}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </div>
                                );
                            }

                        case 'RequestNewLoad':
                            const requestJsonData = JSON.parse(notification.notificationJSON);
                            const requestData = requestJsonData[0].Driver;
                            return (
                                <div className="Notifications" key={`${requestData.DriverId}-${index}`}>
                                    <Grid container
                                        //  justify="space-between"
                                        justifyContent="space-between"
                                        className="Notification-Shift-Start">
                                        <Grid item>
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="subtitle1" display="inline">
                                                    {`(${requestData.DriverId}) ${requestData.DriverName}`}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="body1" display="inline">
                                                    Carrier:{' '}
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {requestData.DriverCompanyName}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item className="text-align-right">
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="body1" display="inline" gutterBottom>
                                                    {moment
                                                        .utc(requestData.ReadyForLoadDateTime)
                                                        .tz('America/Chicago')
                                                        .format('MMMM DD, YYYY h:mm:ss a')}
                                                </Typography>
                                                <Typography variant="subtitle2" display="inline" gutterBottom>
                                                    LOAD REQUEST
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            );
                        case 'SplitLoadRequest':
                            const splitRequestJsonData = JSON.parse(notification.notificationJSON);
                            const splitRequestData = splitRequestJsonData[0].Driver;
                            return (
                                <div className="Notifications" key={`${splitRequestData.DriverId}-${index}`}>
                                    <Grid container
                                        // justify="space-between"
                                        justifyContent="space-between"
                                        className="Notification-Shift-Start">
                                        <Grid item>
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="subtitle1" display="inline">
                                                    {`(${splitRequestData.DriverId}) ${splitRequestData.DriverName}`}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="body1" display="inline">
                                                    Carrier:{' '}
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {splitRequestData.DriverCompanyName}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item className="text-align-right">
                                            <Grid item xs={12} md={12} className="mb-3rem">
                                                <Typography variant="body1" display="inline" gutterBottom>
                                                    {moment
                                                        .utc(notification.notificationDateTime)
                                                        .tz('America/Chicago')
                                                        .format('MMMM DD, YYYY h:mm:ss a')}
                                                </Typography>
                                                <Typography variant="subtitle2" display="inline" gutterBottom>
                                                    SPLIT LOAD REQUEST
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                {/* <Typography variant="body2" display="inline">
                                                        {loadData.LoadId}
                                                    </Typography> */}
                                                <Typography variant="subtitle1" display="inline">
                                                    {notification.loadId}{' '}
                                                    {/* <Link
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            to={{
                                                                pathname: `${Routes.LOAD_PROFILE.route}${loadData.LoadId}`,
                                                            }}
                                                            className="Load-Link"
                                                        >
                                                            <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                                                        </Link> */}
                                                    <span
                                                        className="Load-Link"
                                                        onClick={(): void => openLoadProfile(notification.loadId)}
                                                    >
                                                        <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                                                    </span>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            );
                        // case 'SplitLoadAssigned':
                        //     let splitAssigedJsonData = JSON.parse(notification.notificationJSON);
                        //     let splitAssignedData = splitAssigedJsonData && splitAssigedJsonData[0].Driver;
                        //     return (
                        //         <div className="Notifications" key={`${splitAssignedData.DriverId}-${index}`}>
                        //             <Grid container justify="space-between" className="Notification-Shift-Start">
                        //                 <Grid item>
                        //                     <Grid item xs={12} md={12} className="mb-3rem">
                        //                         <Typography variant="subtitle1" display="inline">
                        //                             {`(${splitAssignedData.DriverId}) ${splitAssignedData.DriverName}`}
                        //                         </Typography>
                        //                     </Grid>
                        //                     <Grid item xs={12} md={12}>
                        //                         <Typography variant="body1" display="inline">
                        //                             Carrier:{' '}
                        //                         </Typography>
                        //                         <Typography variant="body2" display="inline">
                        //                             {splitAssignedData.DriverCompanyName}
                        //                         </Typography>
                        //                     </Grid>
                        //                 </Grid>
                        //                 <Grid item className="text-align-right">
                        //                     <Grid item xs={12} md={12} className="mb-3rem">
                        //                         <Typography variant="body1" display="inline" gutterBottom>
                        //                             {moment
                        //                                 .utc(notification.notificationDateTime)
                        //                                 .tz('America/Chicago')
                        //                                 .format('MMMM DD, YYYY h:mm:ss a')}
                        //                         </Typography>
                        //                         <Typography variant="subtitle2" display="inline" gutterBottom>
                        //                             SPLIT LOAD ASSIGNED
                        //                         </Typography>
                        //                     </Grid>
                        //                     <Grid item xs={12} md={12}>
                        //                         {/* <Typography variant="body2" display="inline">
                        //                                     {loadData.LoadId}
                        //                                 </Typography> */}
                        //                         <Typography variant="subtitle1" display="inline">
                        //                             {notification.LoadId}{' '}
                        //                             {/* <Link
                        //                                         target="_blank"
                        //                                         rel="noopener noreferrer"
                        //                                         to={{
                        //                                             pathname: `${Routes.LOAD_PROFILE.route}${loadData.LoadId}`,
                        //                                         }}
                        //                                         className="Load-Link"
                        //                                     >
                        //                                         <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                        //                                     </Link> */}
                        //                             <span
                        //                                 className="Load-Link"
                        //                                 onClick={(): void => openLoadProfile(notification.LoadId)}
                        //                             >
                        //                                 <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                        //                             </span>
                        //                         </Typography>
                        //                     </Grid>
                        //                 </Grid>
                        //             </Grid>
                        //         </div>
                        //     );

                        default:
                            return null;
                        // break;
                    }
                })}
            </div >
        );
    } else {
        return (
            <div className="Notification-List">
                <Typography variant="body1" align="center">
                    No Notification Found!!
                </Typography>
            </div>
        );
    }
};

export default NotificationListItem;
